import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { AbstractControl, ValidationErrors, Validators } from '@angular/forms'
import { generateUuid } from '@ftr/contracts/type/shared'
import { ButtonDisplayType, fade } from '@ftr/foundation'
import { Observable, ReplaySubject, map, scan, startWith } from 'rxjs'
import { FileOrder, FileUpload, UploadStates } from './file-upload'
import { FileUploadService } from './file-upload.service'

@Component({
  selector: 'ftr-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css'],
  animations: [fade(150, 150)],
})
export class FileUploadComponent<FileUploadPropertiesType, FileUploadResponseType> implements OnInit {
  @Input() control: AbstractControl
  @Input() enabled = true
  @Input() highlightError: Observable<boolean>
  @Input() sizeLimit: number

  /**
   * Limit total number of files which can be uploaded via ftr-file-selector browser or dropped with ftr-drop-overlay.
   * If 1, passes multiple=false to ftr-file-selector to prevent file browser popup from selecting multiple files.
   */
  @Input() maxFiles: number | undefined = undefined

  /**
   * Option for ftr-file-selector: pre-set file browser popup to filter by these file extensions.
   * Also ignores incorrect file types dropped with ftr-drop-overlay.
   */
  @Input() acceptableFileExtensions: string[]

  /**
   * An implementation of the FileUploadService that should be used to upload files with.
   */
  @Input() fileUploadService: FileUploadService<FileUploadPropertiesType, FileUploadResponseType>
  /**
   * The properties required to make the initialize/finalize requests within the
   * FileUploadService implementation used.
   */
  @Input() fileUploadProps: FileUploadPropertiesType
  /**
   * Text to display on the drop overlay
   */
  @Input() dropOverlayText = 'Drop files here'
  @Output() uploading = new EventEmitter<boolean>()

  readonly buttonDisplayType = ButtonDisplayType
  readonly fileOrder = FileOrder

  uploads$: Observable<FileUpload<FileUploadPropertiesType, FileUploadResponseType>[]>
  uploadsSubject$ = new ReplaySubject<FileUpload<FileUploadPropertiesType, FileUploadResponseType>>()

  isReadyToDrop = false

  /**
   * Number of files that have been uploaded or are in the process of uploading, and not cancelled.
   */
  uploadSlotsUsed = 0
  readonly MAX_UPLOAD_SLOTS = 999

  ngOnInit(): void {
    this.setupFilesReducer()
    this.addFiles(this.control.value)
    this.setupUploadingState()
    this.setupValueSetter()
    this.setupUploadValidator()
  }

  setFileReadyToDrop(isReady: boolean): void {
    this.isReadyToDrop = isReady
  }

  get uploadEnabled(): boolean {
    return this.enabled && this.uploadSlotsRemaining > 0
  }

  get uploadSlotsRemaining(): number {
    return (this.maxFiles || this.MAX_UPLOAD_SLOTS) - this.uploadSlotsUsed
  }

  uploadFiles(files: File[]): void {
    for (const file of files) {
      this.uploadFile(file)
    }
  }

  uploadFile(file: File): void {
    if (this.uploadSlotsRemaining < 1) {
      return
    }

    const upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType> = {
      id: generateUuid(),
      file,
      status: UploadStates.Initializing,
      props: this.fileUploadProps,
    }

    this.uploadsSubject$.next(upload)
  }

  isUploading(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return (
      upload.status === UploadStates.Uploading ||
      upload.status === UploadStates.Initializing ||
      upload.status === UploadStates.Finalizing
    )
  }

  isScanning(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return upload.status === UploadStates.Scanning
  }

  isDeleting(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): boolean {
    return upload.status === UploadStates.Deleting
  }

  uploadTrackBy(_: number, item: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): string {
    return item.id
  }

  private setupUploadValidator(): void {
    this.control.setValidators(Validators.compose([this.control.validator, validateUploads]))
  }

  private setupValueSetter(): void {
    this.uploads$.subscribe(fileUploads => {
      this.control.setValue(fileUploads.filter(upload => upload.status !== UploadStates.Canceled))
    })
  }

  private setupFilesReducer(): void {
    this.uploads$ = this.uploadsSubject$.pipe(
      scan(
        (
          acc: FileUpload<FileUploadPropertiesType, FileUploadResponseType>[],
          val: FileUpload<FileUploadPropertiesType, FileUploadResponseType>,
        ) => {
          return updateOrAdd(acc, val)
        },
        [],
      ),
      map(uploads => uploads.filter(upload => upload.status !== UploadStates.Canceled)),
      startWith([]),
    )
  }

  private setupUploadingState(): void {
    this.uploads$.subscribe(fileUploads => {
      this.uploadSlotsUsed = fileUploads.length
      if (fileUploads.find(upload => this.isUploading(upload) || this.isScanning(upload) || this.isDeleting(upload))) {
        this.uploading.emit(true)
      } else {
        this.uploading.emit(false)
      }
    })
  }

  private addFiles(files: FileUpload<FileUploadPropertiesType, FileUploadResponseType>[] | undefined): void {
    if (files) {
      files.forEach(file => this.uploadsSubject$.next(file))
    }
  }

  updateUploadStatus(upload: FileUpload<FileUploadPropertiesType, FileUploadResponseType>): void {
    this.uploadsSubject$.next(upload)
  }
}

function updateOrAdd<T, R>(array: FileUpload<T, R>[], value: FileUpload<T, R>): FileUpload<T, R>[] {
  const index = array.findIndex(f => f.id === value.id)
  if (index > -1) {
    array[index] = value
  } else {
    array.push(value)
  }
  return array
}

function validateUploads(c: AbstractControl): ValidationErrors | null {
  const uploads = c.value as FileUpload[]
  if (uploads.every(upload => upload.status === UploadStates.Success)) {
    return null
  }
  // This ensures the control is still marked as invalid, while not displaying a validation message during upload.
  // The form this is in should be disabled while files are being uploaded anyway.
  return { validateUploads: null }
}
